import { ChevronDown, CheckSquare, Square, Play } from "lucide-react";
import { t } from "@/i18n";

/* ══════════════════════════════════════════════════════════════
   Composants d'interface partagés entre MyErrors, PracticeAlphabet
   et VocabPractice — une seule source pour la carte de groupe
   (checkbox + titre + lecture + déplier) et la boîte de confirmation.
   Placer ce fichier à côté des autres : @/components/GroupUI.jsx
   ══════════════════════════════════════════════════════════════ */

export function GroupShell({
  checkboxState,
  onCheck,
  onOpen,
  onPlay,
  open,
  children,
  header,
}) {
  return (
    <div className="rounded-2xl border bg-card p-3">
      <div className="flex items-start gap-2.5">
        <button
          onClick={onCheck}
          className="shrink-0 text-primary mt-0.5"
          aria-label={t("groupui.selectionner")}
        >
          {checkboxState === "all" ? (
            <CheckSquare size={18} />
          ) : checkboxState === "some" ? (
            <CheckSquare size={18} className="opacity-50" />
          ) : (
            <Square size={18} className="text-muted-foreground" />
          )}
        </button>
        <button onClick={onOpen} className="flex-1 min-w-0 text-left">
          {header}
        </button>
        <div className="flex flex-col items-center gap-1 shrink-0">
          <button
            onClick={onPlay}
            className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center"
            aria-label={t("groupui.jouer_ce_groupe")}
          >
            <Play size={13} fill="currentColor" />
          </button>
          <button
            onClick={onOpen}
            className="w-7 h-7 grid place-items-center rounded-lg hover:bg-secondary"
            aria-label={open ? t("groupui.refermer") : t("groupui.deplier")}
          >
            <ChevronDown
              size={16}
              className={`transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>
      {open && <div className="mt-3 pt-3 border-t">{children}</div>}
    </div>
  );
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}) {
  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-3xl border bg-card p-6 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-head text-lg font-bold mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground mb-6">{message}</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onCancel}
            className="py-3 rounded-2xl border font-semibold"
          >
            {t("groupui.annuler")}
          </button>
          <button
            onClick={onConfirm}
            className="py-3 rounded-2xl bg-destructive text-destructive-foreground font-semibold"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Styles de mise en page (barre d'action fixe + marge basse), identiques
   sur les 3 pages. Un seul <style> injecté par page suffit ; ce texte est
   partagé pour ne pas diverger si on ajuste les hauteurs de nav plus tard. */
export const ACTIONBAR_STYLES = `
  .prac-actionbar {
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 30;
    padding: 0.6rem 1rem
             calc(var(--nav-bottom-mobile, 3.5rem) + env(safe-area-inset-bottom, 0px) + 0.4rem);
    background: hsl(var(--background));
  }
  @supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
    .prac-actionbar {
      background: hsl(var(--background) / 0.95);
      -webkit-backdrop-filter: blur(16px);
      backdrop-filter: blur(16px);
    }
  }
  @media (min-width: 768px) { .prac-actionbar { padding-bottom: 0.9rem; } }
  .prac-page {
    padding-bottom: calc(var(--nav-bottom-mobile, 3.5rem) + env(safe-area-inset-bottom, 0px) + 7rem);
  }
  @media (min-width: 768px) { .prac-page { padding-bottom: 8rem; } }
`;

export const shuffle = (arr) => [...arr].sort(() => 0.5 - Math.random());
